/**
 * CSVテンプレート選択モーダル
 *
 * 既存のCSVテンプレートを選択し適用するためのモーダルダイアログ
 */

import {
  Modal,
  Stack,
  Center,
  Loader,
  Text,
  Select,
  Box,
  Group,
  ScrollArea,
  Table,
  Button,
} from "@mantine/core";
import { CsvTemplateCommon } from "../../services/csvSettingsService";

interface TemplateModalProps {
  opened: boolean;
  onClose: () => void;
  templates: CsvTemplateCommon[];
  loading: boolean;
  selectedTemplateId: string | null;
  onSelectTemplate: (templateId: string | null) => void;
  onApply: () => void;
}

export function TemplateModal({
  opened,
  onClose,
  templates,
  loading,
  selectedTemplateId,
  onSelectTemplate,
  onApply,
}: TemplateModalProps) {
  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="CSVテンプレートを適用"
      size="xl"
    >
      <Stack gap="md">
        {loading ? (
          <Center py="xl">
            <Loader size="sm" />
            <Text size="sm" ml="xs">
              テンプレートを読み込み中...
            </Text>
          </Center>
        ) : templates.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="xl">
            テンプレートが登録されていません。
            「CSVテンプレート管理」から作成してください。
          </Text>
        ) : (
          <>
            <Select
              label="テンプレートを選択"
              placeholder="適用するテンプレートを選択してください"
              data={templates.map((t) => ({ label: t.name, value: t.id }))}
              value={selectedTemplateId}
              onChange={onSelectTemplate}
            />
            {selectedTemplate && (
              <Stack gap="xs">
                <Box
                  p="xs"
                  bg="light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-5))"
                  style={{ borderRadius: "4px" }}
                >
                  <Group gap="xl">
                    <Text size="xs" c="dimmed">
                      項目数:{" "}
                      <Text span fw={500}>
                        {selectedTemplate.columns.length}個
                      </Text>
                    </Text>
                    <Text size="xs" c="dimmed">
                      文字コード:{" "}
                      <Text span fw={500}>
                        {selectedTemplate.encoding === "shift_jis"
                          ? "Shift_JIS"
                          : "UTF-8"}
                      </Text>
                    </Text>
                  </Group>
                </Box>

                <Text size="xs" fw={500}>
                  適用される項目一覧:
                </Text>
                <ScrollArea.Autosize mah={400} type="always">
                  <Table
                    striped
                    withTableBorder
                    verticalSpacing="sm"
                    horizontalSpacing="md"
                  >
                    <Table.Thead
                      bg="light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-6))"
                      style={{ position: "sticky", top: 0, zIndex: 1 }}
                    >
                      <Table.Tr>
                        <Table.Th w={40}>#</Table.Th>
                        <Table.Th>ヘッダー名</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {selectedTemplate.columns.map((col, idx) => (
                        <Table.Tr key={idx}>
                          <Table.Td>{col.order}</Table.Td>
                          <Table.Td>{col.headerName}</Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea.Autosize>
              </Stack>
            )}
            <Group justify="flex-end" mt="sm">
              <Button variant="subtle" onClick={onClose}>
                キャンセル
              </Button>
              <Button onClick={onApply} disabled={!selectedTemplateId}>
                適用する
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
}
